import { Component, inject, OnInit, PLATFORM_ID, signal, DestroyRef } from '@angular/core';
import {
  Router, RouterOutlet, NavigationStart, NavigationEnd, NavigationCancel, NavigationError
} from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import {
  trigger, transition, style, animate, query, group
} from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { ThemeCustomizerService } from './shared/services/theme-customizer.service';
import { ThemeCustomizerComponent } from './shared/components/theme-customizer/theme-customizer.component';
import { AnalyticsService } from './shared/services/analytics.service';
import { AuthService } from 'fireauth-lib';

export const routeFadeAnimation = trigger('routeAnimation', [
  transition('* <=> *', [
    // Pin both elements so neither affects document flow during the crossfade.
    // This prevents the Safari layout-collapse that causes jumpiness when
    // Angular sets position:absolute on the :leave element.
    query(':enter, :leave', [
      style({ position: 'absolute', top: 0, left: 0, right: 0,
              'backface-visibility': 'hidden', '-webkit-backface-visibility': 'hidden' })
    ], { optional: true }),
    query(':enter', [
      style({ opacity: 0 })
    ], { optional: true }),
    group([
      query(':leave', [
        animate('160ms ease-in', style({ opacity: 0 }))
      ], { optional: true }),
      query(':enter', [
        animate('240ms 60ms ease-out', style({ opacity: 1 }))
      ], { optional: true }),
    ])
  ])
]);

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NavbarComponent, FooterComponent, ThemeCustomizerComponent],
  templateUrl: './app.component.html',
  animations: [routeFadeAnimation],
})
export class AppComponent implements OnInit {
  readonly _themeCustomizer = inject(ThemeCustomizerService);
  private readonly _analytics = inject(AnalyticsService);
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  /** True while a route transition (resolver included) is in flight */
  readonly isNavigating = signal(false);

  ngOnInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      this._themeCustomizer.restore();
      this._analytics.init();
      // Restore Firebase Auth session from IndexedDB on every page load/refresh.
      // Uses restoreReaderSession() (not checkIfUserIsLoggedIn) because reader
      // accounts are OTP-verified and never have emailVerified=true in Firebase.
      this._authService.restoreReaderSession();

      // Track route loading for the global progress bar
      this._router.events
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(event => {
          if (event instanceof NavigationStart) {
            this.isNavigating.set(true);
          } else if (
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
          ) {
            this.isNavigating.set(false);
          }
        });
    }
  }

  getRouteAnimData(outlet: RouterOutlet): string {
    return outlet.isActivated
      ? (outlet.activatedRouteData['animation'] ?? outlet.activatedRoute.snapshot.routeConfig?.path ?? '')
      : '';
  }
}
